import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Users, TrendingUp, Filter, Download, Search, 
  ChevronDown, ChevronUp, Calendar, MapPin, Phone, Mail,
  Flame, Snowflake, ThermometerSun, Check, X, Clock,
  DollarSign, BarChart3, PieChart, ArrowUpRight, RefreshCw,
  AlertCircle, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work category labels
const CATEGORY_LABELS = {
  peinture: "Peinture",
  plomberie: "Plomberie",
  electricite: "Électricité",
  chauffage_clim: "Chauffage / Clim",
  menuiserie: "Menuiserie",
  carrelage: "Carrelage",
  maconnerie: "Maçonnerie",
  toiture: "Toiture",
  isolation: "Isolation",
  renovation_globale: "Rénovation globale",
  salle_de_bain: "Salle de bain",
  cuisine: "Cuisine",
  facade: "Façade",
  jardin_exterieur: "Extérieur",
  domotique: "Domotique",
  autre: "Autre"
};

// Status labels and colors
const STATUS_CONFIG = {
  new: { label: "Nouveau", color: "bg-blue-500", textColor: "text-blue-400" },
  qualified: { label: "Qualifié", color: "bg-purple-500", textColor: "text-purple-400" },
  contacted: { label: "Contacté", color: "bg-amber-500", textColor: "text-amber-400" },
  converted: { label: "Converti", color: "bg-green-500", textColor: "text-green-400" },
  sold: { label: "Vendu", color: "bg-emerald-500", textColor: "text-emerald-400" },
  closed: { label: "Fermé", color: "bg-zinc-500", textColor: "text-zinc-400" }
};

// Score icons and colors
const SCORE_CONFIG = {
  hot: { icon: Flame, color: "text-red-500", bgColor: "bg-red-500/10", label: "Chaud" },
  medium: { icon: ThermometerSun, color: "text-amber-500", bgColor: "bg-amber-500/10", label: "Moyen" },
  cold: { icon: Snowflake, color: "text-blue-400", bgColor: "bg-blue-500/10", label: "Froid" }
};

export default function AdminLeads() {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Leads list
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    score: '',
    department: '',
    search: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  
  // Selected lead for detail view
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Refresh flag
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch dashboard
  useEffect(() => {
    fetchDashboard();
  }, [refreshKey]);

  // Fetch leads when filters change
  useEffect(() => {
    fetchLeads();
  }, [filters, page, refreshKey]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/dashboard`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 403) {
        setError("Accès réservé aux administrateurs");
        return;
      }
      
      if (!response.ok) throw new Error('Erreur de chargement');
      
      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.score) params.append('score', filters.score);
      if (filters.department) params.append('department', filters.department);
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      
      const response = await fetch(`${API_URL}/api/admin/leads?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 403) {
        setError("Accès réservé aux administrateurs");
        return;
      }
      
      if (!response.ok) throw new Error('Erreur de chargement');
      
      const data = await response.json();
      setLeads(data.leads);
      setTotalLeads(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${leadId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Erreur de mise à jour');
      
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert(err.message);
    }
  };

  const exportLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.score) params.append('score', filters.score);
      if (filters.department) params.append('department', filters.department);
      
      const response = await fetch(`${API_URL}/api/admin/leads/export?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Erreur d\'export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Accès refusé</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Dashboard Leads</h1>
            <p className="text-zinc-500">Gestion et suivi des leads qualifiés</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setRefreshKey(k => k + 1)}
              variant="outline"
              className="border-zinc-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={exportLeads}
              className="bg-emerald-500 hover:bg-emerald-400"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <Users className="w-6 h-6 text-zinc-500" />
                <span className="text-xs text-zinc-500">Total</span>
              </div>
              <p className="text-3xl font-bold text-white">{dashboard.summary.total}</p>
              <p className="text-sm text-zinc-500">leads enregistrés</p>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <Calendar className="w-6 h-6 text-blue-500" />
                <span className="text-xs text-blue-400">Aujourd'hui</span>
              </div>
              <p className="text-3xl font-bold text-white">{dashboard.summary.today}</p>
              <p className="text-sm text-zinc-500">nouveaux leads</p>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
                <span className="text-xs text-emerald-400">Cette semaine</span>
              </div>
              <p className="text-3xl font-bold text-white">{dashboard.summary.this_week}</p>
              <p className="text-sm text-zinc-500">leads générés</p>
            </div>
            
            <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <Flame className="w-6 h-6 text-red-500" />
                <span className="text-xs text-red-400">HOT</span>
              </div>
              <p className="text-3xl font-bold text-white">{dashboard.summary.hot_sellable}</p>
              <p className="text-sm text-red-300">leads vendables</p>
            </div>
          </div>
        )}

        {/* Charts Row */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* By Status */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Par statut
              </h3>
              <div className="space-y-2">
                {Object.entries(dashboard.by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[status]?.color || 'bg-zinc-500'}`} />
                      <span className="text-sm text-zinc-300">{STATUS_CONFIG[status]?.label || status}</span>
                    </div>
                    <span className="text-sm font-semibold text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Score */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
                <PieChart className="w-4 h-4" />
                Par score
              </h3>
              <div className="space-y-3">
                {Object.entries(dashboard.by_score).map(([score, count]) => {
                  const config = SCORE_CONFIG[score];
                  const Icon = config?.icon || ThermometerSun;
                  return (
                    <div key={score} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${config?.color}`} />
                        <span className="text-sm text-zinc-300">{config?.label || score}</span>
                      </div>
                      <span className="text-sm font-semibold text-white">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By Category (Top 5) */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4" />
                Top catégories
              </h3>
              <div className="space-y-2">
                {Object.entries(dashboard.by_category).slice(0, 5).map(([cat, count]) => (
                  <div key={cat} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300 truncate">{CATEGORY_LABELS[cat] || cat}</span>
                    <span className="text-sm font-semibold text-amber-400">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-zinc-400">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filtres</span>
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            
            <select
              value={filters.score}
              onChange={(e) => setFilters({...filters, score: e.target.value})}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
            >
              <option value="">Tous les scores</option>
              {Object.entries(SCORE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
            >
              <option value="">Toutes catégories</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Lead</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Localisation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Catégorie</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Score</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Budget</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Statut</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-zinc-500">
                      Chargement...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-zinc-500">
                      Aucun lead trouvé
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => {
                    const scoreConfig = SCORE_CONFIG[lead.lead_score] || SCORE_CONFIG.medium;
                    const ScoreIcon = scoreConfig.icon;
                    const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                    
                    return (
                      <tr key={lead.lead_id} className="hover:bg-zinc-800/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{lead.name}</div>
                          <div className="text-xs text-zinc-500">{lead.lead_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-zinc-300">
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-zinc-400">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-zinc-300">
                            <MapPin className="w-3 h-3" />
                            {lead.city || '-'}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {lead.postal_code} {lead.department && `(${lead.department})`}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded text-xs font-medium">
                            {CATEGORY_LABELS[lead.work_category] || lead.work_category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-1 px-2 py-1 ${scoreConfig.bgColor} rounded`}>
                            <ScoreIcon className={`w-3 h-3 ${scoreConfig.color}`} />
                            <span className={`text-xs font-medium ${scoreConfig.color}`}>
                              {scoreConfig.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {lead.estimated_budget_min && lead.estimated_budget_max ? (
                            <span className="text-sm text-emerald-400">
                              {lead.estimated_budget_min.toLocaleString()} - {lead.estimated_budget_max.toLocaleString()} €
                            </span>
                          ) : (
                            <span className="text-sm text-zinc-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={lead.status}
                            onChange={(e) => updateLeadStatus(lead.lead_id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-medium bg-zinc-800 border border-zinc-700 ${statusConfig.textColor}`}
                          >
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                              <option key={key} value={key}>{cfg.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-500">
                          {formatDate(lead.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedLead(lead)}
                            className="text-zinc-400 hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalLeads > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-sm text-zinc-500">
                {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalLeads)} sur {totalLeads}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  className="border-zinc-700"
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(page + 1) * pageSize >= totalLeads}
                  onClick={() => setPage(p => p + 1)}
                  className="border-zinc-700"
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Détail du lead</h2>
                <button onClick={() => setSelectedLead(null)} className="text-zinc-500 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Nom</p>
                    <p className="text-white font-medium">{selectedLead.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Email</p>
                    <p className="text-white">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Téléphone</p>
                    <p className="text-white">{selectedLead.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Localisation</p>
                    <p className="text-white">{selectedLead.city || '-'} {selectedLead.postal_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Catégorie</p>
                    <p className="text-amber-400">{CATEGORY_LABELS[selectedLead.work_category] || selectedLead.work_category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Budget estimé</p>
                    <p className="text-emerald-400">
                      {selectedLead.estimated_budget_min && selectedLead.estimated_budget_max 
                        ? `${selectedLead.estimated_budget_min.toLocaleString()} - ${selectedLead.estimated_budget_max.toLocaleString()} €`
                        : '-'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-2">Description du projet</p>
                  <p className="text-zinc-300 bg-zinc-800 p-4 rounded-lg">{selectedLead.project_summary}</p>
                </div>
                
                {selectedLead.lead_score_reasons && selectedLead.lead_score_reasons.length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase mb-2">Raisons du score</p>
                    <ul className="space-y-1">
                      {selectedLead.lead_score_reasons.map((reason, i) => (
                        <li key={i} className="text-sm text-zinc-400">• {reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
